import React from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";

import { fadeUp } from "../utils/animations";

const MotionBox = motion(Box);

const Footer = () => {
  const year = new Date().getFullYear();

  const navigation = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Services", path: "/projects" },
    { label: "Pricing", path: "/pricing" },
    { label: "Contact", path: "/contact" },
  ];

  const contactItems = [
    {
      label: "Email",
      value: "officialwiyou.team@gmail.com",
      href: "mailto:officialwiyou.team@gmail.com",
      icon: EmailOutlinedIcon,
    },
    {
      label: "Phone",
      value: "+91 76899 19139",
      href: "tel:+917689919139",
      icon: PhoneRoundedIcon,
    },
    {
      label: "Phone",
      value: "+91 83197 30346",
      href: "tel:+918319730346",
      icon: PhoneRoundedIcon,
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        mt: { xs: 7, md: 12 },
        px: { xs: 1.5, sm: 2, md: 3 },
        pb: 0,
      }}
    >
      <MotionBox
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.15,
        }}
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#211A16",
          color: "#FFFDF8",
          borderRadius: {
            xs: "26px 26px 0 0",
            md: "36px 36px 0 0",
          },
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* =========================================================
            TOP BRAND / CTA AREA
        ========================================================= */}

        <Box
          sx={{
            position: "relative",
            px: {
              xs: 3,
              sm: 4,
              md: 6,
              lg: 8,
            },
            pt: {
              xs: 4,
              sm: 5,
              md: 7,
            },
            pb: {
              xs: 4,
              md: 6,
            },
          }}
        >
          {/* Decorative giant mark */}
          <Typography
            aria-hidden="true"
            sx={{
              position: "absolute",
              right: {
                xs: -15,
                md: 15,
              },
              top: {
                xs: 25,
                md: 15,
              },
              fontSize: {
                xs: "110px",
                sm: "150px",
                md: "210px",
              },
              lineHeight: 0.8,
              fontWeight: 900,
              letterSpacing: "-0.1em",
              color: "rgba(255,255,255,0.025)",
              userSelect: "none",
              pointerEvents: "none",
              textAlign: "right",
            }}
          >
            WiYou!
          </Typography>

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: "900px",
            }}
          >

            <Typography
              component="h2"
              sx={{
                maxWidth: "850px",
                fontSize: {
                  xs: "40px",
                  sm: "54px",
                  md: "70px",
                  lg: "82px",
                },
                lineHeight: 0.96,
                fontWeight: 800,
                letterSpacing: "-0.06em",
                color: "#FFFDF8",
                mb: 2.5,
              }}
            >
              Let's build something
              <Box
                component="span"
                sx={{
                  display: "block",
                  color: "#B9896C",
                }}
              >
                worth remembering.
              </Box>
            </Typography>

            <Typography
              sx={{
                maxWidth: "580px",
                fontSize: {
                  xs: "14px",
                  md: "16px",
                },
                lineHeight: 1.75,
                color: "#AFA39B",
              }}
            >
              Modern websites, applications and digital
              experiences — designed with intention and
              built to actually work.
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor:
              "rgba(255,255,255,0.09)",
          }}
        />

        {/* =========================================================
            MAIN FOOTER GRID
        ========================================================= */}

        <Box
          sx={{
            px: {
              xs: 3,
              sm: 4,
              md: 6,
              lg: 8,
            },
            py: {
              xs: 4,
              md: 5,
            },
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1.2fr 0.8fr",
              lg: "1.35fr 0.65fr 1fr 1fr",
            },
            gap: {
              xs: 4,
              md: 5,
              lg: 7,
            },
          }}
        >
          {/* =====================================================
              BRAND
          ===================================================== */}

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <Box
                component="img"
                src="/assets/logo.png"
                alt="WiYou! Tech Studios"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "13px",
                  objectFit: "contain",
                  backgroundColor: "#FFFDF8",
                  p: 0.5,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 800,
                    letterSpacing:
                      "-0.02em",
                    color: "#FFFDF8",
                  }}
                >
                  WiYou! Tech
                </Typography>

                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing:
                      "0.12em",
                    textTransform:
                      "uppercase",
                    color: "#81756D",
                  }}
                >
                  Studios
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                maxWidth: "330px",
                fontSize: "13px",
                lineHeight: 1.75,
                color: "#948982",
              }}
            >
              A commitment to provide the
              best web technologies to all —
              from the first idea to a
              production-ready launch.
            </Typography>
          </Box>

          {/* =====================================================
              NAVIGATION
          ===================================================== */}

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#A06E50",
                mb: 2,
              }}
            >
              Explore
            </Typography>

            <Stack spacing={0.5}>
              {navigation.map((item) => (
                <Box
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    py: 0.8,
                    color: "#D7D0CB",
                    textDecoration: "none",
                    fontSize: "13px",
                    transition:
                      "all 180ms ease",

                    "&:hover": {
                      color: "#FFFDF8",
                      transform:
                        "translateX(4px)",
                    },
                  }}
                >
                  {item.label}

                  <ArrowOutwardRoundedIcon
                    sx={{
                      fontSize: 14,
                      opacity: 0,
                      transition:
                        "opacity 180ms ease",
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>

          {/* =====================================================
              CONTACT
          ===================================================== */}

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#A06E50",
                mb: 2,
              }}
            >
              Contact
            </Typography>

            <Stack spacing={1.2}>
              {contactItems.map(
                (item, index) => {
                  const Icon = item.icon;

                  return (
                    <Box
                      key={`${item.value}-${index}`}
                      component="a"
                      href={item.href}
                      sx={{
                        display: "flex",
                        alignItems:
                          "center",
                        gap: 1.5,
                        p: 1,
                        mx: -1,
                        borderRadius:
                          1,
                        color: "inherit",
                        textDecoration:
                          "none",
                        transition:
                          "all 180ms ease",

                        "&:hover": {
                          backgroundColor:
                            "rgba(255,255,255,0.045)",
                          transform:
                            "translateX(3px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          flexShrink: 0,
                          display: "grid",
                          placeItems:
                            "center",
                          borderRadius:
                            "10px",
                          backgroundColor:
                            "rgba(255,255,255,0.06)",
                          border:
                            "0px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 16,
                            color:
                              "#C2B2A7",
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize:
                              "9px",
                            fontWeight: 700,
                            letterSpacing:
                              "0.1em",
                            textTransform:
                              "uppercase",
                            color:
                              "#756A63",
                            mb: 0.2,
                          }}
                        >
                          {item.label}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize:
                              "12px",
                            color:
                              "#D7D0CB",
                            overflow:
                              "hidden",
                            textOverflow:
                              "ellipsis",
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              )}
            </Stack>
          </Box>

          {/* =====================================================
              WHATSAPP / SOCIAL
          ===================================================== */}

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#A06E50",
                mb: 2,
              }}
            >
              Let's connect
            </Typography>

            <Typography
              sx={{
                maxWidth: "260px",
                fontSize: "13px",
                lineHeight: 1.7,
                color: "#948982",
                mb: 2,
              }}
            >
              Have a project in mind?
              Start a conversation with
              the team.
            </Typography>

            <Box
              component="a"
              href="https://wa.me/917689919139"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: "fit-content",
                pr: 1.5,
                p: 1,
                borderRadius: "14px",
                border:
                  "0px solid rgba(255,255,255,0.1)",
                backgroundColor:
                  "rgba(255, 253, 250, 0.05)",
                color: "#FFFDF8",
                textDecoration: "none",
                transition:
                  "all 220ms ease",

                "&:hover": {
                  backgroundColor:
                    "rgba(255,255,255,0.07)",
                  borderColor:
                    "rgba(255,255,255,0.18)",
                  transform:
                    "translateY(-2px)",
                },
              }}
            >
              <IconButton
                disableRipple
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 0.4,
                  backgroundColor:
                    "rgba(34,197,94,0.1)",
                  border:
                    "0px solid rgba(34,197,94,0.22)",
                  color: "#4ADE80",

                  "&:hover": {
                    backgroundColor:
                      "rgba(34,197,94,0.1)",
                  },
                }}
              >
                <WhatsAppIcon
                  sx={{
                    fontSize: 18,
                  }}
                />
              </IconButton>

              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  Chat on WhatsApp
                </Typography>

                <Typography
                  sx={{
                    fontSize: "9px",
                    color: "#756A63",
                    mt: 0.3,
                  }}
                >
                  Usually replies quickly
                </Typography>
              </Box>

              <ArrowOutwardRoundedIcon
                sx={{
                  ml: 1,
                  fontSize: 15,
                  color: "#81756D",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* =========================================================
            BOTTOM BAR
        ========================================================= */}

        <Divider
          sx={{
            mx: {
              xs: 3,
              sm: 4,
              md: 6,
              lg: 8,
            },
            borderColor:
              "rgba(255,255,255,0.09)",
          }}
        />

        <Box
          sx={{
            px: {
              xs: 3,
              sm: 4,
              md: 6,
              lg: 8,
            },
            py: 2.5,
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent:
              "space-between",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              color: "#756A63",
            }}
          >
            © {year} WiYou! Tech Studios.
            All rights reserved.
          </Typography>

          <Typography
            sx={{
              fontSize: "10px",
              color: "#756A63",
            }}
          >
            Crafted with{" "}
            <Box
              component="span"
              sx={{
                color: "#B9896C",
                mx: 0.4,
              }}
            >
              ♥
            </Box>{" "}
            by the WiYou! team.
          </Typography>
        </Box>
      </MotionBox>
    </Box>
  );
};

export default Footer;